import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, MenuItem, Select, InputLabel, FormControl, Tooltip, Grid, Container, InputAdornment, Chip
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// API base is available in authService if needed elsewhere
import { apiGet, apiPost, apiUpload, apiFetch } from '../api/apiClient';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import StarIcon from '@mui/icons-material/Star';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PolicyIcon from '@mui/icons-material/Policy';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import AiroNotesManagement from '../components/AiroNotesManagement';
import { AddBlogDialog, EditBlogDialog } from '../components/BlogDialogs';

const sampleUpdates = [
  {
    title: 'Star of the Month Announced!',
    details: "Congratulations to Priya Sharma for being selected as October's Star of the Month!",
    icon: 'star',
    media: null,
    duration: '48',
  },
  {
    title: 'Annual Benefits Enrollment Open',
    details: 'Please review and update your benefits selections by Oct 31st.',
    icon: 'gift',
    media: null,
    duration: '72',
  },
  {
    title: 'Remote Work Policy Update',
    details: 'New guidelines for hybrid and remote work effective Nov 1st.',
    icon: 'policy',
    media: null,
    duration: '24',
  },
  {
    title: 'Upcoming Holiday Schedule',
    details: 'Office will be closed for Diwali on Nov 12th.',
    icon: 'event',
    media: null,
    duration: '24',
  },
];

export default function Admin() {
  // ...existing state and hooks...

  // Delete link by id
  const handleDeleteLink = async (id) => {
    if (!id) return;
    setLinkSubmitting(true);
    setLinkError('');
    try {
      await apiFetch(`/important_links/${id}`, { method: 'DELETE' });
      await fetchAllLinks();
    } catch (e) {
      setLinkError(e.message || 'Unable to delete link');
    } finally {
      setLinkSubmitting(false);
    }
  };
  const [tab, setTab] = useState(0);
  // HR Updates
  const [hrUpdates, setHrUpdates] = useState(sampleUpdates);
  const [hrLoading, setHrLoading] = useState(false);
  const [hrListError, setHrListError] = useState('');
  const [links, setLinks] = useState([]);
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [filteredLinks, setFilteredLinks] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  // Dialogs
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [viewIdx, setViewIdx] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  // For editing
  const [editTitle, setEditTitle] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [editIcon, setEditIcon] = useState('star');
  const [editMedia, setEditMedia] = useState(null);
  const [editDuration, setEditDuration] = useState('24');
  const handleView = idx => setViewIdx(idx);
  const handleCloseView = () => setViewIdx(null);
  const handleEdit = idx => {
    setEditIdx(idx);
    const u = hrUpdates[idx];
    setEditTitle(u.title);
    setEditDetails(u.details);
    setEditIcon(u.icon);
    setEditMedia(u.media || null);
    setEditDuration(u.duration || '24');
  };
  const handleCloseEdit = () => setEditIdx(null);
  const handleSaveEdit = () => {
    setHrUpdates(hrUpdates.map((u, i) => i === editIdx ? {
      ...u,
      title: editTitle,
      details: editDetails,
      icon: editIcon,
      media: editMedia,
      duration: editDuration
    } : u));
    setEditIdx(null);
  };
  const handleDelete = async (idx) => {
    try {
      setHrListError('');
      const update = hrUpdates[idx];
      if (!update?.id) {
        // fallback: remove client-side for sample rows
        setHrUpdates(hrUpdates.filter((_, i) => i !== idx));
        return;
      }
      setHrLoading(true);
      await apiFetch(`/admin_updates/${update.id}`, { method: 'DELETE' });
      await fetchAllHrUpdates();
    } catch (e) {
      setHrListError(e.message || 'Unable to delete update');
    } finally {
      setHrLoading(false);
    }
  };
  // (Archive helper removed — not used)
  // New update/post fields
  const [hrTitle, setHrTitle] = useState('');
  const [hrDetails, setHrDetails] = useState('');
  const [hrIcon, setHrIcon] = useState('star');
  const [hrMediaFiles, setHrMediaFiles] = useState([]); // multiple files
  const [hrDuration, setHrDuration] = useState(null); // Date | null
  const [hrSubmitting, setHrSubmitting] = useState(false);
  const [hrError, setHrError] = useState('');
  // Link fields
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkError, setLinkError] = useState('');

  // User Management state
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsListError, setAdminsListError] = useState('');
  const [openAdminDialog, setOpenAdminDialog] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminEmailError, setAdminEmailError] = useState('');

  // AiroNotes Management state
  const [blogs, setBlogs] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogListError, setBlogListError] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [openBlogDialog, setOpenBlogDialog] = useState(false);
  const [blogTitle, setBlogTitle] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogSubmitting, setBlogSubmitting] = useState(false);
  const [blogError, setBlogError] = useState('');
  const [editBlogIdx, setEditBlogIdx] = useState(null);
  const [editBlogTitle, setEditBlogTitle] = useState('');
  const [editBlogContent, setEditBlogContent] = useState('');
  const blogSortOrder = 'newest'; // 'newest' or 'oldest'

  const iconOptions = [
    { value: 'star', label: 'Star', icon: <StarIcon sx={{ color: '#FFD700' }} /> },
    { value: 'settings', label: 'Settings', icon: <SettingsIcon sx={{ color: '#616161' }} /> },
    { value: 'gift', label: 'Gift', icon: <CardGiftcardIcon sx={{ color: '#1976d2' }} /> },
    { value: 'policy', label: 'Policy', icon: <PolicyIcon sx={{ color: '#43a047' }} /> },
    { value: 'event', label: 'Event', icon: <EventNoteIcon sx={{ color: '#ff9800' }} /> },
    { value: 'link', label: 'Link', icon: <LinkIcon sx={{ color: '#0d004a' }} /> },
  ];

  const getIcon = (icon) => {
    switch (icon) {
      case 'star': return <StarIcon sx={{ color: '#FFD700' }} />;
      case 'settings': return <SettingsIcon sx={{ color: '#616161' }} />;
      case 'gift': return <CardGiftcardIcon sx={{ color: '#1976d2' }} />;
      case 'policy': return <PolicyIcon sx={{ color: '#43a047' }} />;
      case 'event': return <EventNoteIcon sx={{ color: '#ff9800' }} />;
      case 'link': return <LinkIcon sx={{ color: '#0d004a' }} />;
      default: return null;
    }
  };

  // Fetch all HR updates from API
  const fetchAllHrUpdates = async () => {
    try {
      setHrListError('');
      setHrLoading(true);
      const data = await apiGet('/admin_updates');
      const mapped = Array.isArray(data)
        ? data.map((item) => ({
            id: item.id,
            title: item.title,
            details: item.message,
            icon: item.icon_name || 'link',
            media: Array.isArray(item.media) ? item.media.map((m) => m.file_url) : [],
            duration: item.valid_till,
          }))
        : [];
      setHrUpdates(mapped);
    } catch (e) {
      setHrListError(e.message || 'Unable to load updates');
    } finally {
      setHrLoading(false);
    }
  };

  useEffect(() => {
    fetchAllHrUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter updates based on search term
  useEffect(() => {
    if (tab === 0) {
      const filtered = hrUpdates.filter(update => {
        const searchLower = searchTerm.toLowerCase();
        return (
          update.title?.toLowerCase().includes(searchLower) ||
          update.details?.toLowerCase().includes(searchLower) ||
          update.icon?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredUpdates(filtered);
    }
  }, [searchTerm, hrUpdates, tab]);

  // Filter links based on search term
  useEffect(() => {
    if (tab === 1) {
      const filtered = links.filter(link => {
        const searchLower = searchTerm.toLowerCase();
        return (
          link.title?.toLowerCase().includes(searchLower) ||
          link.url?.toLowerCase().includes(searchLower) ||
          link.description?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredLinks(filtered);
    }
  }, [searchTerm, links, tab]);

  // Filter admins based on search term
  useEffect(() => {
    if (tab === 2) {
      const filtered = admins.filter(admin => {
        const searchLower = searchTerm.toLowerCase();
        return (
          admin.name?.toLowerCase().includes(searchLower) ||
          admin.email?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredAdmins(filtered);
    }
  }, [searchTerm, admins, tab]);

  // Filter and sort blogs based on search term and sort order
  useEffect(() => {
    if (tab === 3) {
      let filtered = blogs.filter(blog => {
        const searchLower = searchTerm.toLowerCase();
        return (
          blog.title?.toLowerCase().includes(searchLower) ||
          blog.content?.toLowerCase().includes(searchLower)
        );
      });
      // Apply sorting
      const sorted = [...filtered].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return blogSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      });
      setFilteredBlogs(sorted);
    }
  }, [searchTerm, blogs, tab, blogSortOrder]);

  // Reset search when tab changes
  useEffect(() => {
    setSearchTerm('');
  }, [tab]);

  const handleAddHrUpdate = async () => {
    try {
      setHrError('');
      if (!hrTitle.trim() || !hrDetails.trim()) {
        setHrError('Title and Details are required');
        return;
      }
      setHrSubmitting(true);
      // 1) Upload media if any
      let fileUrls = [];
      if (hrMediaFiles && hrMediaFiles.length > 0) {
        const formData = new FormData();
        hrMediaFiles.forEach((f) => formData.append('files', f));
        const uploadData = await apiUpload('/admin_updates/upload', formData);
        // Expected shape: { files: [{ fileName, url }] }
        fileUrls = Array.isArray(uploadData?.files)
          ? uploadData.files.map((f) => f.url)
          : Array.isArray(uploadData?.fileUrls)
          ? uploadData.fileUrls
          : [];
      }

      // 2) Create update
      const validTillIso = hrDuration instanceof Date
        ? hrDuration.toISOString()
        : (typeof hrDuration === 'string' && !isNaN(Date.parse(hrDuration))
            ? new Date(hrDuration).toISOString()
            : null);

      const payload = {
        type: 'update',
        title: hrTitle.trim(),
        message: hrDetails.trim(),
        icon_name: hrIcon,
        valid_till: validTillIso,
        fileUrls,
      };

      await apiPost('/admin_updates', payload);

      // Refresh list from server to reflect persisted data
      await fetchAllHrUpdates();

      // Reset form
      setHrTitle('');
      setHrDetails('');
      setHrIcon('star');
      setHrMediaFiles([]);
      setHrDuration(null);
      setOpenUpdateDialog(false);
    } catch (e) {
      setHrError(e.message || 'Something went wrong');
    } finally {
      setHrSubmitting(false);
    }
  };
  // Fetch all links from backend
  const fetchAllLinks = async () => {
    try {
      setLinkError('');
      const data = await apiGet('/important_links');
      // data is expected to be an array
      setLinks(Array.isArray(data) ? data : []);
    } catch (e) {
      setLinkError(e.message || 'Unable to load links');
    }
  };

  // Sort blogs based on sort order
  const sortBlogs = (blogsToSort, order) => {
    const sorted = [...blogsToSort];
    sorted.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return order === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  };

  useEffect(() => {
    fetchAllLinks();
    fetchAllBlogs();
    fetchAllAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch admins from backend
  const fetchAllAdmins = async () => {
    try {
      setAdminsListError('');
      setAdminsLoading(true);
      const data = await apiGet('/employees/admins');
      if (Array.isArray(data)) {
        const mapped = data.map(item => ({
          id: item.employee_code,
          name: item.employee_name,
          email: item.office_email,
          designation: item.designation_id,
          department: item.department,
          reporting_to: item.reporting_to,
          city_id: item.city_id,
          is_admin: item.is_admin,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
        }));
        setAdmins(mapped);
      } else {
        setAdmins([]);
        setAdminsListError('No admins returned from server');
      }
    } catch (e) {
      setAdminsListError(e.message || 'Unable to load admins');
      setAdmins([]);
    } finally {
      setAdminsLoading(false);
    }
  };

  // Fetch all blogs from API (fallback to localStorage/hardcoded)
  const fetchAllBlogs = async () => {
    try {
      setBlogListError('');
      setBlogLoading(true);
      // Fetch from API (API-only approach)
      const data = await apiGet('/blogs');
      if (Array.isArray(data)) {
        const mapped = data.map(b => ({
          id: b.id,
          title: b.title,
          content: b.content,
          authorCode: b.author_code,
          is_published: b.is_published,
          createdAt: b.created_at || b.createdAt || new Date().toISOString(),
        }));
        const sorted = sortBlogs(mapped, blogSortOrder);
        setBlogs(sorted);
      } else {
        // No data returned — treat as empty list
        setBlogs([]);
        setBlogListError('No AiroNotes returned from server');
      }
    } catch (e) {
      // API failed — show error and empty list (no local fallback)
      setBlogListError(e.message || 'Unable to load AiroNotes from server');
      setBlogs([]);
    } finally {
      setBlogLoading(false);
    }
  };

  // Add new blog
  const handleAddBlog = async () => {
    setBlogError('');
    if (!blogTitle.trim() || !blogContent.trim()) {
      setBlogError('Title and Content are required');
      return;
    }
    setBlogSubmitting(true);
    try {
      const payload = {
        title: blogTitle.trim(),
        content: blogContent.trim(),
        is_published: true,
      };
      // POST to API
      await apiPost('/blogs', payload);
      // Refresh from server
      await fetchAllBlogs();
      // Reset form
      setBlogTitle('');
      setBlogContent('');
      setOpenBlogDialog(false);
    } catch (e) {
        // API failed — surface error (no local fallback)
        setBlogError(e.message || 'Unable to add AiroNote');
    } finally {
      setBlogSubmitting(false);
    }
  };

  // Delete blog (persist to backend)
  const handleDeleteBlog = async (id) => {
    if (!id) return;
    setBlogListError('');
    setBlogLoading(true);
    try {
      await apiFetch(`/blogs/${id}`, { method: 'DELETE' });
      // Refresh from server to ensure canonical state
      await fetchAllBlogs();
    } catch (e) {
      setBlogListError(e.message || 'Unable to delete AiroNote');
    } finally {
      setBlogLoading(false);
    }
  };

  // Edit blog - opens dialog
  const handleEditBlog = (idx) => {
    setEditBlogIdx(idx);
    const blog = blogs[idx];
    setEditBlogTitle(blog.title);
    setEditBlogContent(blog.content);
  };

  const handleCloseEditBlog = () => {
    setEditBlogIdx(null);
    setEditBlogTitle('');
    setEditBlogContent('');
    setBlogError('');
  };

  // Save edited blog (persist to backend)
  const handleSaveEditBlog = async () => {
    setBlogError('');
    if (!editBlogTitle.trim() || !editBlogContent.trim()) {
      setBlogError('Title and Content are required');
      return;
    }
    if (editBlogIdx === null) return;
    const blogToEdit = blogs[editBlogIdx];
    if (!blogToEdit || !blogToEdit.id) {
      setBlogError('Invalid blog selected for edit');
      return;
    }

    setBlogSubmitting(true);
    try {
      const payload = {
        title: editBlogTitle.trim(),
        content: editBlogContent.trim(),
        // preserve publish state if present
        is_published: typeof blogToEdit.is_published !== 'undefined' ? blogToEdit.is_published : true,
      };

      await apiFetch(`/blogs/${blogToEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Refresh canonical list from server
      await fetchAllBlogs();
      handleCloseEditBlog();
    } catch (e) {
      setBlogError(e.message || 'Unable to save AiroNote');
    } finally {
      setBlogSubmitting(false);
    }
  };

  // Add new link via backend
  const handleAddLink = async () => {
    if (!linkLabel.trim() || !linkUrl.trim()) {
      setLinkError('Label and URL are required');
      return;
    }
    setLinkSubmitting(true);
    setLinkError('');
    try {
      const payload = {
        title: linkLabel.trim(),
        url: linkUrl.trim(),
        description: linkDescription.trim(),
      };
      await apiPost('/important_links', payload);
      // Refresh links from backend
      await fetchAllLinks();
      setLinkLabel('');
      setLinkUrl('');
      setLinkDescription('');
      setOpenLinkDialog(false);
    } catch (e) {
      setLinkError(e.message || 'Something went wrong');
    } finally {
      setLinkSubmitting(false);
    }
  };



  return (
    <Container maxWidth="xl" sx={{ py: 3, mt: 8 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          bgcolor: '#0D004A',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
              Admin Panel
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
              Manage updates, links, and user permissions
            </Typography>
          </Box>
          
          {/* Search Bar - Right Aligned */}
          <TextField
            placeholder={tab === 0 ? "Search updates..." : tab === 1 ? "Search links..." : tab === 2 ? "Search admins..." : "Search AiroNotes..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: 350,
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
              }
            }}
          />
        </Box>
      </Paper>

      {/* Section Tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              minHeight: 48,
              '&.Mui-selected': {
                color: '#0D004A',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0D004A',
              height: 3,
            },
          }}
        >
          <Tab label="Updates" />
          <Tab label="Links" />
          <Tab label="User Management" />
          <Tab label="AiroNotes Management" />
        </Tabs>
      </Box>

      {/* Action Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {tab === 0 && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpenUpdateDialog(true)}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Add Update
          </Button>
        )}
        {tab === 1 && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpenLinkDialog(true)}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Add Link
          </Button>
        )}
        {tab === 2 && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpenAdminDialog(true)}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Add Admin
          </Button>
        )}
        {tab === 3 && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpenBlogDialog(true)}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Add AiroNote
          </Button>
        )}
      </Box>

      {tab === 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          {hrListError && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="error">
                {hrListError}
              </Typography>
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                }
              }}>
                <TableCell sx={{ width: '60px' }}>Icon</TableCell>
                <TableCell sx={{ width: '200px' }}>Title</TableCell>
                <TableCell sx={{ width: '300px', maxWidth: '300px' }}>Details</TableCell>
                <TableCell align="center" sx={{ width: '150px' }}>Visible Until</TableCell>
                <TableCell align="center" sx={{ width: '150px', whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hrLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">Loading updates…</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!hrLoading && (searchTerm ? filteredUpdates : hrUpdates).map((u, i) => {
                const originalIndex = hrUpdates.findIndex(update => update === u);
                return (
                  <TableRow
                    key={i}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa',
                      },
                      '&:nth-of-type(even):hover': {
                        backgroundColor: '#f0f0f0',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '60px' }}>{getIcon(u.icon)}</TableCell>
                    <TableCell sx={{ width: '200px' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#0D004A' }}>
                        {u.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '300px', maxWidth: '300px' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666', 
                          lineHeight: 1.5,
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {u.details}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '150px' }}>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                        {(() => {
                          const d = u.duration;
                          if (!d) return '-';
                          if (d instanceof Date && !isNaN(d)) return d.toLocaleString();
                          if (typeof d === 'string' && !isNaN(Date.parse(d))) return new Date(d).toLocaleString();
                          return String(d);
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '150px', whiteSpace: 'nowrap' }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(originalIndex)} sx={{ color: '#0D004A', p: 0.5 }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(originalIndex)} sx={{ color: '#1976d2', p: 0.5 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(originalIndex)} sx={{ color: '#d32f2f', p: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!hrLoading && (searchTerm ? filteredUpdates : hrUpdates).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <AdminPanelSettingsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                      {searchTerm ? 'No updates found' : 'No updates yet'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      {searchTerm ? 'Try adjusting your search terms' : 'Add your first update to get started'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                }
              }}>
                <TableCell>Title</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(searchTerm ? filteredLinks : links).map((l, i) => (
                <TableRow
                  key={l.id || i}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:nth-of-type(even):hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#0D004A' }}>
                      {l.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#1976d2',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': { color: '#1565c0' }
                      }}
                      onClick={() => window.open(l.url, '_blank')}
                    >
                      {l.url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
                      {l.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteLink(l.id)} 
                        disabled={linkSubmitting}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {(searchTerm ? filteredLinks : links).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <LinkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                      {searchTerm ? 'No links found' : 'No links yet'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      {searchTerm ? 'Try adjusting your search terms' : 'Add your first link to get started'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                }
              }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminsLoading && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">Loading admins…</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!adminsLoading && adminsListError && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="error">{adminsListError}</Typography>
                  </TableCell>
                </TableRow>
              )}

              {!adminsLoading && (searchTerm ? filteredAdmins : admins).map((a, i) => {
                const originalIndex = admins.findIndex(admin => admin === a);
                return (
                  <TableRow
                    key={i}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa',
                      },
                      '&:nth-of-type(even):hover': {
                        backgroundColor: '#f0f0f0',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#0D004A' }}>
                        {a.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {a.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          onClick={() => setAdmins(admins.filter((_, idx) => idx !== originalIndex))}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(searchTerm ? filteredAdmins : admins).length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                    <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                      {searchTerm ? 'No admins found' : 'No admins yet'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      {searchTerm ? 'Try adjusting your search terms' : 'Add your first admin to get started'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 3 && (
        <AiroNotesManagement
          blogs={blogs}
          filteredBlogs={filteredBlogs}
          blogLoading={blogLoading}
          blogListError={blogListError}
          searchTerm={searchTerm}
          tab={tab}
          handleEditBlog={handleEditBlog}
          handleDeleteBlog={handleDeleteBlog}
        />
      )}

      {/* View Details Dialog */}
      <Dialog 
        open={viewIdx !== null} 
        onClose={handleCloseView} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Update Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseView}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {viewIdx !== null && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {getIcon(hrUpdates[viewIdx].icon)}
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0D004A' }}>
                  {hrUpdates[viewIdx].title}
                </Typography>
              </Box>
              
              <Box sx={{ 
                p: 3, 
                bgcolor: '#F8F9FA', 
                borderRadius: 2,
                border: '1px solid #E9ECEF',
                mb: 3
              }}>
                <Typography variant="body1" sx={{ 
                  color: '#333', 
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap'
                }}>
                  {hrUpdates[viewIdx].details}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D004A', minWidth: 120 }}>
                    Icon:
                  </Typography>
                  <Chip 
                    label={hrUpdates[viewIdx].icon}
                    size="small"
                    sx={{ bgcolor: '#E3F2FD', color: '#0D004A', fontWeight: 500 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D004A', minWidth: 120 }}>
                    Media:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {(() => {
                      const m = hrUpdates[viewIdx].media;
                      if (!m) return '-';
                      if (Array.isArray(m)) return m.length > 0 ? `${m.length} file(s)` : '-';
                      return m.name ? m.name : '-';
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D004A', minWidth: 120 }}>
                    Visible Until:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {(() => {
                      const d = hrUpdates[viewIdx].duration;
                      if (!d) return '-';
                      if (d instanceof Date && !isNaN(d)) return d.toLocaleString();
                      if (typeof d === 'string' && !isNaN(Date.parse(d))) return new Date(d).toLocaleString();
                      return String(d);
                    })()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 4 }}>
          <Button 
            onClick={handleCloseView}
            variant="contained"
            sx={{
              bgcolor: '#0D004A',
              '&:hover': { bgcolor: '#1a0a6e' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editIdx !== null} 
        onClose={handleCloseEdit} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Update
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEdit}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            label="Title"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Details"
            value={editDetails}
            onChange={e => setEditDetails(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<ImageIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Upload Media
                <input
                  type="file"
                  hidden
                  onChange={e => setEditMedia(e.target.files[0])}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Visible Until"
                  value={editDuration || null}
                  onChange={val => setEditDuration(val)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          {editMedia && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selected: {editMedia.name}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Icon</InputLabel>
            <Select
              value={editIcon}
              label="Icon"
              onChange={e => setEditIcon(e.target.value)}
            >
              {iconOptions.map(opt => (
                <MenuItem value={opt.value} key={opt.value}>
                  {opt.icon} {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 4 }}>
          <Button 
            onClick={handleCloseEdit}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            sx={{
              bgcolor: '#0D004A',
              '&:hover': { bgcolor: '#1a0a6e' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Update/Post Dialog */}
      <Dialog 
        open={openUpdateDialog} 
        onClose={() => setOpenUpdateDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Update
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOpenUpdateDialog(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            label="Title"
            value={hrTitle}
            onChange={e => setHrTitle(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Details"
            value={hrDetails}
            onChange={e => setHrDetails(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<ImageIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Upload Media
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={e => setHrMediaFiles(Array.from(e.target.files || []))}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Visible Until"
                  value={hrDuration || null}
                  onChange={val => setHrDuration(val)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          {hrMediaFiles?.length > 0 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selected: {hrMediaFiles.map(f => f.name).join(', ')}
            </Typography>
          )}
          {hrError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {hrError}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Icon</InputLabel>
            <Select
              value={hrIcon}
              label="Icon"
              onChange={e => setHrIcon(e.target.value)}
            >
              {iconOptions.map(opt => (
                <MenuItem value={opt.value} key={opt.value}>
                  {opt.icon} {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 4 }}>
          <Button 
            onClick={() => setOpenUpdateDialog(false)} 
            disabled={hrSubmitting}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddHrUpdate} 
            variant="contained" 
            disabled={hrSubmitting}
            sx={{
              bgcolor: '#0D004A',
              '&:hover': { bgcolor: '#1a0a6e' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            {hrSubmitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog 
        open={openLinkDialog} 
        onClose={() => setOpenLinkDialog(false)} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Important Link
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOpenLinkDialog(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            label="Label"
            value={linkLabel}
            onChange={e => setLinkLabel(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={linkSubmitting}
          />
          <TextField
            label="URL"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={linkSubmitting}
          />
          <TextField
            label="Description"
            value={linkDescription}
            onChange={e => setLinkDescription(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={linkSubmitting}
          />
          {linkError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {linkError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 4 }}>
          <Button 
            onClick={() => setOpenLinkDialog(false)} 
            disabled={linkSubmitting}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddLink} 
            variant="contained" 
            disabled={linkSubmitting}
            sx={{
              bgcolor: '#0D004A',
              '&:hover': { bgcolor: '#1a0a6e' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            {linkSubmitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog 
        open={openAdminDialog} 
        onClose={() => setOpenAdminDialog(false)} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Admin
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOpenAdminDialog(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            label="Name"
            value={adminName}
            onChange={e => setAdminName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email"
            value={adminEmail}
            onChange={e => {
              setAdminEmail(e.target.value);
              setAdminEmailError('');
            }}
            error={!!adminEmailError}
            helperText={adminEmailError}
            fullWidth
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 4 }}>
          <Button 
            onClick={() => setOpenAdminDialog(false)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;
              if (!adminName.trim() || !adminEmail.trim()) {
                if (!adminEmail.trim()) setAdminEmailError('Email is required');
                return;
              }
              if (!emailRegex.test(adminEmail)) {
                setAdminEmailError('Enter a valid email');
                return;
              }
              if (admins.some(a => a.email.toLowerCase() === adminEmail.toLowerCase())) {
                setAdminEmailError('An admin with this email already exists');
                return;
              }
              setAdmins([...admins, { name: adminName.trim(), email: adminEmail.trim() }]);
              setAdminName('');
              setAdminEmail('');
              setOpenAdminDialog(false);
            }}
            sx={{
              bgcolor: '#0D004A',
              '&:hover': { bgcolor: '#1a0a6e' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blog dialogs moved to separate component for clarity */}
      <AddBlogDialog
        open={openBlogDialog}
        onClose={() => setOpenBlogDialog(false)}
        blogTitle={blogTitle}
        setBlogTitle={setBlogTitle}
        blogContent={blogContent}
        setBlogContent={setBlogContent}
        blogError={blogError}
        blogSubmitting={blogSubmitting}
        onAdd={handleAddBlog}
      />

      <EditBlogDialog
        open={editBlogIdx !== null}
        onClose={handleCloseEditBlog}
        editBlogTitle={editBlogTitle}
        setEditBlogTitle={setEditBlogTitle}
        editBlogContent={editBlogContent}
        setEditBlogContent={setEditBlogContent}
        blogError={blogError}
        blogSubmitting={blogSubmitting}
        onSave={handleSaveEditBlog}
      />

    </Container>
  );
}
